import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaList,
  FaEdit,
  FaMoneyBillWave,
  FaUniversity,
  FaCalendar,
  FaCheck,
  FaTimes,
  FaClock,
  FaHistory,
  FaExchangeAlt,
} from "react-icons/fa";

const ExternalTransactions = () => {
  const [activeTab, setActiveTab] = useState("transactions");
  const [transactions, setTransactions] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [chequeStatusModal, setChequeStatusModal] = useState({
    show: false,
    chequeId: null,
    status: null,
    notes: "",
  });

  const [rescheduleModal, setRescheduleModal] = useState({
    show: false,
    chequeId: null,
    oldChequeNumber: "",
    newChequeNumber: "",
    newChequeDate: "",
    newBankName: "",
    newBankBranch: "",
    notes: "",
  });

  // Form state
  const [formData, setFormData] = useState({
    customer_id: "",
    direction: "in", // in: customer -> us, out: us -> customer
    asset_type: "cash",
    asset_id: "",
    payment_method: "cash",
    amount: "",
    reason: "",
    // Cheque specific fields
    cheque_number: "",
    cheque_date: "",
    bank_name: "",
    bank_branch: "",
    // Card specific fields
    card_transaction_id: "",
    card_bank_name: "",
    card_holder_name: "",
    // Bank transfer specific fields
    from_bank_details: "",
    to_bank_details: "",
    transaction_reference: "",
  });

  useEffect(() => {
    fetchTransactions();
    fetchCheques();
    fetchCustomers();
    fetchCashAccounts();
    fetchBankAccounts();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/external-transactions`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setTransactions(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCheques = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/external-cheques`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCheques(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching cheques:", error);
      setCheques([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/customers`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomers(Array.isArray(data?.customers) ? data.customers : []);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  const fetchCashAccounts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCashAccounts(
          Array.isArray(data?.cash_accounts) ? data.cash_accounts : []
        );
      }
    } catch (error) {
      console.error("Error fetching cash accounts:", error);
      setCashAccounts([]);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setBankAccounts(
          Array.isArray(data?.bank_accounts) ? data.bank_accounts : []
        );
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      setBankAccounts([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Perform validation before submitting
    const validationResult = validateTransaction();
    if (validationResult && validationResult.type === "error") {
      alert(validationResult.message);
      return;
    }

    try {
      setLoading(true);
      const method = editingTransaction ? "PUT" : "POST";
      const url = editingTransaction
        ? `${process.env.REACT_APP_BASE_URL}/api/assets/external-transactions/${editingTransaction.id}`
        : `${process.env.REACT_APP_BASE_URL}/api/assets/external-transactions`;

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchTransactions();
        fetchCheques();
        setShowForm(false);
        setEditingTransaction(null);
        resetForm();
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to save transaction");
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      alert("An error occurred while saving the transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleChequeStatusUpdate = async (chequeId, status, notes = "") => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/external-cheques/clear`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            cheque_id: chequeId,
            status,
            notes: notes || null,
          }),
        }
      );

      if (response.ok) {
        fetchCheques();
        fetchTransactions();
        setChequeStatusModal({
          show: false,
          chequeId: null,
          status: null,
          notes: "",
        });
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to update cheque status");
      }
    } catch (error) {
      console.error("Error updating cheque status:", error);
    } finally {
      setLoading(false);
    }
  };

  const openChequeStatusModal = (chequeId, status) => {
    setChequeStatusModal({
      show: true,
      chequeId,
      status,
      notes: "",
    });
  };

  const handleChequeStatusConfirm = () => {
    handleChequeStatusUpdate(
      chequeStatusModal.chequeId,
      chequeStatusModal.status,
      chequeStatusModal.notes
    );
  };

  const openRescheduleModal = (cheque) => {
    setRescheduleModal({
      show: true,
      chequeId: cheque.id,
      oldChequeNumber: cheque.cheque_number,
      newChequeNumber: "",
      newChequeDate: "",
      newBankName: cheque.bank_name || "",
      newBankBranch: cheque.bank_branch || "",
      notes: "",
    });
  };

  const handleRescheduleSubmit = async () => {
    try {
      setLoading(true);

      if (!rescheduleModal.newChequeNumber || !rescheduleModal.newChequeDate) {
        alert("Please fill in the new cheque number and date");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/external-cheques/reschedule`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            old_cheque_id: rescheduleModal.chequeId,
            new_cheque_number: rescheduleModal.newChequeNumber,
            new_cheque_date: rescheduleModal.newChequeDate,
            new_bank_name: rescheduleModal.newBankName,
            new_bank_branch: rescheduleModal.newBankBranch,
            notes: rescheduleModal.notes,
          }),
        }
      );

      if (response.ok) {
        fetchCheques();
        fetchTransactions();
        setRescheduleModal({
          show: false,
          chequeId: null,
          oldChequeNumber: "",
          newChequeNumber: "",
          newChequeDate: "",
          newBankName: "",
          newBankBranch: "",
          notes: "",
        });
        alert("Cheque rescheduled successfully");
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to reschedule cheque");
      }
    } catch (error) {
      console.error("Error rescheduling cheque:", error);
      alert("An error occurred while rescheduling the cheque");
    } finally {
      setLoading(false);
    }
  };

  const fetchChequeHistory = async (chequeId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/external-cheques/${chequeId}/history`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const history = await response.json();
        // You can display this in a modal or alert
        console.log("Cheque History:", history);
        // For now, just show an alert with basic info
        const historyText = history
          .map(
            (h) =>
              `${h.cheque_type}: ${h.cheque_number} (${
                h.cheque_date
              }) - Status: ${h.status || "Pending"}`
          )
          .join("\n");
        alert(`Cheque History:\n${historyText}`);
      }
    } catch (error) {
      console.error("Error fetching cheque history:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      direction: "in",
      asset_type: "cash",
      asset_id: "",
      payment_method: "cash",
      amount: "",
      reason: "",
      cheque_number: "",
      cheque_date: "",
      bank_name: "",
      bank_branch: "",
      card_transaction_id: "",
      card_bank_name: "",
      card_holder_name: "",
      from_bank_details: "",
      to_bank_details: "",
      transaction_reference: "",
    });
  };

  // Get current account details for validation
  const getCurrentAccount = () => {
    if (!formData.asset_type || !formData.asset_id) return null;
    const accounts =
      formData.asset_type === "cash" ? cashAccounts : bankAccounts;
    return accounts.find((acc) => acc.id === parseInt(formData.asset_id));
  };

  // Validation function
  const validateTransaction = () => {
    const { direction, amount, payment_method } = formData;
    const transferAmount = parseFloat(amount);

    if (!transferAmount || transferAmount <= 0) {
      return null; // No validation needed for empty or invalid amounts
    }

    const currentAccount = getCurrentAccount();
    if (!currentAccount) {
      return { type: "error", message: "Please select an account" };
    }

    const currentBalance = parseFloat(currentAccount.amount);

    // For outgoing transactions (us -> customer), check balance and minimum limits
    if (direction === "out") {
      // Skip balance check for cheques with future dates
      const isFutureCheque =
        payment_method === "cheque" &&
        formData.cheque_date &&
        new Date(formData.cheque_date) > new Date();

      if (!isFutureCheque) {
        // Check if we have sufficient balance
        if (currentBalance < transferAmount) {
          return {
            type: "error",
            message: `Insufficient balance. Available: ${currentBalance.toFixed(
              2
            )}`,
          };
        }

        // Check minimum limit for cash accounts - this applies regardless of current balance
        if (formData.asset_type === "cash" && currentAccount.min_limit) {
          const minLimit = parseFloat(currentAccount.min_limit);
          const remainingBalance = currentBalance - transferAmount;

          // If current balance is already below minimum limit, we cannot send money
          if (currentBalance < minLimit) {
            return {
              type: "error",
              message: `Cannot send money. Current balance (${currentBalance.toFixed(
                2
              )}) is already below minimum limit (${minLimit.toFixed(
                2
              )}). Please receive funds first.`,
            };
          }

          // If transaction would cause balance to go below minimum limit
          if (remainingBalance < minLimit) {
            return {
              type: "error",
              message: `Transaction would violate minimum limit. Remaining balance (${remainingBalance.toFixed(
                2
              )}) would be below minimum limit (${minLimit.toFixed(2)})`,
            };
          }
        }
      }
    }

    // For incoming transactions (customer -> us), check maximum limits only
    if (direction === "in" && formData.asset_type === "cash") {
      if (currentAccount.max_limit) {
        const maxLimit = parseFloat(currentAccount.max_limit);
        const newBalance = currentBalance + transferAmount;
        if (newBalance > maxLimit) {
          return {
            type: "error",
            message: `Transaction would exceed maximum limit. New balance (${newBalance.toFixed(
              2
            )}) would exceed maximum limit (${maxLimit.toFixed(2)})`,
          };
        }
      }

      // For incoming transactions, we show a success message even if current balance is below minimum
      // This allows customers to send money to bring the account above minimum limit
      if (currentAccount.min_limit) {
        const minLimit = parseFloat(currentAccount.min_limit);
        const newBalance = currentBalance + transferAmount;

        if (currentBalance < minLimit && newBalance >= minLimit) {
          return {
            type: "success",
            message: `Transaction will bring balance above minimum limit. New balance: ${newBalance.toFixed(
              2
            )}`,
          };
        } else if (currentBalance < minLimit && newBalance < minLimit) {
          return {
            type: "warning",
            message: `Transaction accepted, but balance will still be below minimum limit (${minLimit.toFixed(
              2
            )}). New balance: ${newBalance.toFixed(2)}`,
          };
        }
      }
    }

    return { type: "success", message: "Transaction amount is valid" };
  };

  // Get validation message for real-time feedback
  const getValidationMessage = () => {
    return validateTransaction();
  };

  const canUpdateChequeStatus = (chequeDate) => {
    const today = new Date();
    const cheque = new Date(chequeDate);
    return cheque <= today;
  };

  const canRescheduleCheque = (cheque) => {
    // Can reschedule if:
    // 1. Cheque is not already cleared
    // 2. Cheque is not already rescheduled (or if it is, then it's the new one)
    return cheque.status !== "cleared" && !cheque.next_cheque_number;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "cleared":
        return "text-green-600 bg-green-100";
      case "bounced":
        return "text-red-600 bg-red-100";
      case "expired":
        return "text-gray-600 bg-gray-100";
      case "rescheduled":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-blue-600 bg-blue-100";
    }
  };

  const renderChequeStatusModal = () => {
    if (!chequeStatusModal.show) return null;

    const getStatusLabel = (status) => {
      switch (status) {
        case "cleared":
          return "Clear";
        case "bounced":
          return "Mark as Bounced";
        case "expired":
          return "Mark as Expired";
        case "rescheduled":
          return "Reschedule";
        default:
          return "Update Status";
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case "cleared":
          return "bg-green-600 hover:bg-green-700";
        case "bounced":
          return "bg-red-600 hover:bg-red-700";
        case "expired":
          return "bg-gray-600 hover:bg-gray-700";
        case "rescheduled":
          return "bg-yellow-600 hover:bg-yellow-700";
        default:
          return "bg-blue-600 hover:bg-blue-700";
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-semibold mb-4">
            {getStatusLabel(chequeStatusModal.status)} Cheque
          </h3>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={chequeStatusModal.notes}
              onChange={(e) =>
                setChequeStatusModal({
                  ...chequeStatusModal,
                  notes: e.target.value,
                })
              }
              placeholder="Add any notes about this status update..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows="3"
              maxLength="500"
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {chequeStatusModal.notes.length}/500 characters
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleChequeStatusConfirm}
              disabled={loading}
              className={`flex-1 text-white px-4 py-2 rounded-lg font-medium transition-colors ${getStatusColor(
                chequeStatusModal.status
              )} disabled:opacity-50`}
            >
              {loading ? "Updating..." : "Confirm"}
            </button>
            <button
              onClick={() =>
                setChequeStatusModal({
                  show: false,
                  chequeId: null,
                  status: null,
                  notes: "",
                })
              }
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRescheduleModal = () => {
    if (!rescheduleModal.show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
          <h3 className="text-lg font-semibold mb-4">Reschedule Cheque</h3>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700">
              Current Cheque
            </h4>
            <p className="text-sm text-gray-600">
              Cheque Number: {rescheduleModal.oldChequeNumber}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Cheque Number *
              </label>
              <input
                type="text"
                value={rescheduleModal.newChequeNumber}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newChequeNumber: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new cheque number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Cheque Date *
              </label>
              <input
                type="date"
                value={rescheduleModal.newChequeDate}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newChequeDate: e.target.value,
                  })
                }
                min={new Date().toISOString().split("T")[0]}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                value={rescheduleModal.newBankName}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newBankName: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bank name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Branch
              </label>
              <input
                type="text"
                value={rescheduleModal.newBankBranch}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    newBankBranch: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bank branch"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Rescheduling
              </label>
              <textarea
                value={rescheduleModal.notes}
                onChange={(e) =>
                  setRescheduleModal({
                    ...rescheduleModal,
                    notes: e.target.value,
                  })
                }
                placeholder="Explain why the cheque is being rescheduled..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="3"
                maxLength="500"
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {rescheduleModal.notes.length}/500 characters
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleRescheduleSubmit}
              disabled={
                loading ||
                !rescheduleModal.newChequeNumber ||
                !rescheduleModal.newChequeDate
              }
              className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Rescheduling..." : "Reschedule Cheque"}
            </button>
            <button
              onClick={() =>
                setRescheduleModal({
                  show: false,
                  chequeId: null,
                  oldChequeNumber: "",
                  newChequeNumber: "",
                  newChequeDate: "",
                  newBankName: "",
                  newBankBranch: "",
                  notes: "",
                })
              }
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTransactionForm = () => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold mb-4">
        {editingTransaction ? "Edit Transaction" : "Add New Transaction"}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer
            </label>
            <select
              value={formData.customer_id}
              onChange={(e) =>
                setFormData({ ...formData, customer_id: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Direction
            </label>
            <select
              value={formData.direction}
              onChange={(e) =>
                setFormData({ ...formData, direction: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="in">Money In (Customer → Us)</option>
              <option value="out">Money Out (Us → Customer)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asset Type
            </label>
            <select
              value={formData.asset_type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  asset_type: e.target.value,
                  asset_id: "",
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="cash">Cash</option>
              <option value="bank">Bank</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {formData.asset_type === "cash" ? "Cash Account" : "Bank Account"}
            </label>
            <select
              value={formData.asset_id}
              onChange={(e) =>
                setFormData({ ...formData, asset_id: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">
                Select {formData.asset_type === "cash" ? "Cash" : "Bank"}{" "}
                Account
              </option>
              {(formData.asset_type === "cash"
                ? cashAccounts
                : bankAccounts
              ).map((account) => (
                <option key={account.id} value={account.id}>
                  {formData.asset_type === "cash"
                    ? account.name
                    : `${account.bank_name} - ${account.account_number}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) =>
                setFormData({ ...formData, payment_method: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {/* Real-time validation feedback */}
            {formData.amount &&
              getCurrentAccount() &&
              getValidationMessage() && (
                <div
                  className={`mt-2 p-2 border rounded text-sm ${
                    getValidationMessage().type === "error"
                      ? "bg-red-50 border-red-200 text-red-700"
                      : getValidationMessage().type === "warning"
                      ? "bg-yellow-50 border-yellow-200 text-yellow-700"
                      : "bg-green-50 border-green-200 text-green-700"
                  }`}
                >
                  {getValidationMessage().message}
                </div>
              )}
            {/* Display account information */}
            {getCurrentAccount() && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                <div className="font-medium">Account Details:</div>
                <div>
                  Current Balance:
                  {parseFloat(getCurrentAccount().amount).toFixed(2)}
                </div>
                {formData.asset_type === "cash" &&
                  getCurrentAccount().min_limit && (
                    <div>
                      Minimum Limit:
                      {parseFloat(getCurrentAccount().min_limit).toFixed(2)}
                    </div>
                  )}
                {formData.asset_type === "cash" &&
                  getCurrentAccount().max_limit && (
                    <div>
                      Maximum Limit:
                      {parseFloat(getCurrentAccount().max_limit).toFixed(2)}
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>

        {formData.payment_method === "cheque" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cheque Number
              </label>
              <input
                type="text"
                value={formData.cheque_number}
                onChange={(e) =>
                  setFormData({ ...formData, cheque_number: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={formData.payment_method === "cheque"}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cheque Date
              </label>
              <input
                type="date"
                value={formData.cheque_date}
                onChange={(e) =>
                  setFormData({ ...formData, cheque_date: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={formData.payment_method === "cheque"}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                value={formData.bank_name}
                onChange={(e) =>
                  setFormData({ ...formData, bank_name: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Branch
              </label>
              <input
                type="text"
                value={formData.bank_branch}
                onChange={(e) =>
                  setFormData({ ...formData, bank_branch: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {formData.payment_method === "card" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Transaction ID
              </label>
              <input
                type="text"
                value={formData.card_transaction_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    card_transaction_id: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Bank Name
              </label>
              <input
                type="text"
                value={formData.card_bank_name}
                onChange={(e) =>
                  setFormData({ ...formData, card_bank_name: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Holder Name
              </label>
              <input
                type="text"
                value={formData.card_holder_name}
                onChange={(e) =>
                  setFormData({ ...formData, card_holder_name: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {formData.payment_method === "bank_transfer" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Bank Details
              </label>
              <input
                type="text"
                value={formData.from_bank_details}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    from_bank_details: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Source bank details"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Bank Details
              </label>
              <input
                type="text"
                value={formData.to_bank_details}
                onChange={(e) =>
                  setFormData({ ...formData, to_bank_details: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Destination bank details"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Reference
              </label>
              <input
                type="text"
                value={formData.transaction_reference}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    transaction_reference: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bank reference number"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason/Description
          </label>
          <textarea
            value={formData.reason}
            onChange={(e) =>
              setFormData({ ...formData, reason: e.target.value })
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
          />
        </div>

        <div className="flex space-x-2">
          <button
            type="submit"
            disabled={
              loading ||
              (getValidationMessage() &&
                getValidationMessage().type === "error")
            }
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : editingTransaction ? "Update" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowForm(false);
              setEditingTransaction(null);
              resetForm();
            }}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  const renderTransactionsList = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">External Transactions</h3>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <FaPlus className="w-4 h-4" />
            <span>Add Transaction</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Direction
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Payment Method
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(transaction.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {transaction.customer_name || "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      transaction.direction === "in"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {transaction.direction === "in" ? "Money In" : "Money Out"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    {transaction.asset_type === "cash" ? (
                      <FaMoneyBillWave className="w-4 h-4 mr-2 text-green-600" />
                    ) : (
                      <FaUniversity className="w-4 h-4 mr-2 text-blue-600" />
                    )}
                    {transaction.account_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex flex-col">
                    <span>{transaction.payment_method}</span>
                    {transaction.payment_method === "cheque" && (
                      <div className="flex items-center space-x-1 mt-1">
                        {transaction.status && (
                          <span
                            className={`text-xs ${
                              transaction.status === "cleared"
                                ? "text-green-600"
                                : transaction.status === "bounced"
                                ? "text-red-600"
                                : transaction.status === "expired"
                                ? "text-gray-600"
                                : transaction.status === "rescheduled"
                                ? "text-yellow-600"
                                : "text-blue-600"
                            }`}
                          >
                            ({transaction.status})
                          </span>
                        )}
                        {!transaction.status && (
                          <span className="text-xs text-blue-600">
                            (pending)
                          </span>
                        )}
                        {transaction.cheque_reschedule_indicator === "rescheduled" && (
                          <span className="text-xs text-orange-600 font-medium">
                            ↻ rescheduled
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  ${parseFloat(transaction.amount).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setEditingTransaction(transaction);
                      setFormData({
                        customer_id: transaction.customer_id || "",
                        direction: transaction.direction,
                        asset_type: transaction.asset_type,
                        asset_id: transaction.asset_id,
                        payment_method: transaction.payment_method,
                        amount: transaction.amount,
                        reason: transaction.reason || "",
                        cheque_number: transaction.cheque_number || "",
                        cheque_date: transaction.cheque_date || "",
                        bank_name: transaction.bank_name || "",
                        bank_branch: transaction.bank_branch || "",
                        card_transaction_id:
                          transaction.card_transaction_id || "",
                        card_bank_name: transaction.card_bank_name || "",
                        card_holder_name: transaction.card_holder_name || "",
                        from_bank_details: transaction.from_bank_details || "",
                        to_bank_details: transaction.to_bank_details || "",
                        transaction_reference:
                          transaction.transaction_reference || "",
                      });
                      setShowForm(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderChequesList = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Cheque Management</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Cheque Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Bank
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Reschedule Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Notes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cheques.map((cheque) => (
              <tr key={cheque.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {cheque.cheque_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(cheque.cheque_date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {cheque.bank_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  ${parseFloat(cheque.amount_paid).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {cheque.previous_cheque_number && (
                    <div className="text-yellow-600">
                      <span className="text-xs">Rescheduled from:</span>
                      <br />
                      <span className="font-medium">
                        {cheque.previous_cheque_number}
                      </span>
                    </div>
                  )}
                  {cheque.next_cheque_number && (
                    <div className="text-blue-600">
                      <span className="text-xs">Rescheduled to:</span>
                      <br />
                      <span className="font-medium">
                        {cheque.next_cheque_number}
                      </span>
                    </div>
                  )}
                  {!cheque.previous_cheque_number &&
                    !cheque.next_cheque_number && (
                      <span className="text-gray-400">-</span>
                    )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      cheque.status
                    )}`}
                  >
                    {cheque.status || "pending"}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  {cheque.notes ? (
                    <div className="truncate" title={cheque.notes}>
                      {cheque.notes}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {!cheque.status &&
                    canUpdateChequeStatus(cheque.cheque_date) && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() =>
                            openChequeStatusModal(cheque.id, "cleared")
                          }
                          className="text-green-600 hover:text-green-900"
                          title="Mark as Cleared"
                        >
                          <FaCheck className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            openChequeStatusModal(cheque.id, "bounced")
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Mark as Bounced"
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            openChequeStatusModal(cheque.id, "expired")
                          }
                          className="text-gray-600 hover:text-gray-900"
                          title="Mark as Expired"
                        >
                          <FaClock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openRescheduleModal(cheque)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Reschedule"
                          disabled={!canRescheduleCheque(cheque)}
                        >
                          <FaExchangeAlt className="w-4 h-4" />
                        </button>
                        {(cheque.is_rescheduled ||
                          cheque.previous_cheque_number) && (
                          <button
                            onClick={() =>
                              fetchChequeHistory(cheque.payment_id)
                            }
                            className="text-blue-600 hover:text-blue-900"
                            title="View Cheque History"
                          >
                            <FaHistory className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  {!canUpdateChequeStatus(cheque.cheque_date) && (
                    <span className="text-gray-400 text-xs">Future date</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200/60">
        <div className="p-6 border-b border-slate-200/60">
          <h2 className="text-2xl font-bold text-slate-800">
            External Transactions
          </h2>
          <p className="text-slate-600 mt-1">
            Manage transactions with customers involving cash and bank accounts
          </p>
        </div>

        <div className="p-6">
          <div className="flex space-x-4 mb-6">
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === "transactions"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={() => setActiveTab("transactions")}
            >
              <FaList className="w-4 h-4 inline mr-2" />
              Transactions
            </button>
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === "cheques"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={() => setActiveTab("cheques")}
            >
              <FaCalendar className="w-4 h-4 inline mr-2" />
              Cheques
            </button>
          </div>

          {showForm && renderTransactionForm()}
          {!showForm &&
            activeTab === "transactions" &&
            renderTransactionsList()}
          {!showForm && activeTab === "cheques" && renderChequesList()}
        </div>
      </div>

      {/* Cheque Status Update Modal */}
      {renderChequeStatusModal()}

      {/* Cheque Reschedule Modal */}
      {renderRescheduleModal()}
    </div>
  );
};

export default ExternalTransactions;
